import { html, unsafeCSS } from 'lit';
import { property } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import {
  BaseElement,
  customElement,
  SlotObserver,
} from '../../../components/base-element';
import '../../../components/icon/icon.wc';
import '../../../components/text/text-title.wc';
import '../../../components/text/text-subtitle.wc';
import styles from './result.scss?inline';

@customElement('ps-result')
export class ResultWC extends SlotObserver(BaseElement, [
  '[slot]',
  '[slot="footer"]',
  '[slot="image"]',
]) {
  static styles = unsafeCSS(styles);

  /**
   * Result status, decide icons and colors
   */
  @property() status: 'info' | 'success' | 'error' = 'info';

  /** Set text-align for result content */
  @property({ reflect: true }) align: 'start' | 'center' = 'start';

  /**
   * Set component label
   */
  @property() label = '';

  /**
   * Heading level for the label. Omitted, the label stays a paragraph.
   */
  @property() labelTag?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';

  /**
   * Set component body text
   */
  @property() body = '';

  render() {
    const classes = classMap({
      'c-result': true,
      [`c-result--status-${this.status}`]: this.status,
      [`c-result--align-${this.align}`]: this.align,
    });

    const iconName = this.status === 'error' ? 'exclamation' : 'check';

    const iconClasses = classMap({
      'c-result__icon': true,
      [`c-result__icon--${this.status}`]: this.status,
    });

    const label = this.label
      ? html`
          <ps-text-title
            size="small"
            textAlign=${this.align}
            tag=${ifDefined(this.labelTag)}
            >${this.label}</ps-text-title
          >
          <ps-spacer size="small"></ps-spacer>
        `
      : null;

    const body = this.body
      ? html`<ps-text-subtitle variant="secondary" textAlign=${this.align}
          >${this.body}</ps-text-subtitle
        >`
      : null;

    return html`
      <div class=${classes}>
        ${this.hasSlottedContent('[slot="image"]')
          ? html`
              <div class="c-result__image">
                <slot name="image"></slot>
              </div>
              <ps-spacer size="xlarge"></ps-spacer>
            `
          : html`
              <div class=${iconClasses}>
                <ps-icon name=${iconName} size="medium"></ps-icon>
              </div>
            `}
        ${label} ${body}
        <slot></slot>
        ${this.hasSlottedContent('[slot="footer"]')
          ? html`
              <div class="c-result__footer">
                <slot name="footer"></slot>
              </div>
            `
          : null}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'ps-result': ResultWC;
  }
}
